// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/** Modal body HTML. Glossary terms are marked with .gloss for click-to-define. */

const g = (term: string, label: string): string => `<span class="gloss" data-term="${term}">${label}</span>`;

export function howItWorksHtml(): string {
  return /* html */ `
    <ol class="steps">
      <li>
        <strong>Pick your instrument.</strong> Chromatic works for anything; choose Guitar, Bass,
        Ukulele or Violin and Tunewell also lights up the ${g('pitch', 'string')} you're nearest to.
      </li>
      <li>
        <strong>Play a note.</strong> Tap <em>Start microphone</em> and allow access, then pluck or bow a
        single string. Tunewell reads the sound in short slices, many times a second.
      </li>
      <li>
        <strong>Find the pitch.</strong> Each slice runs through McLeod's pitch method
        (${g('nsdf', 'NSDF')}) to lock onto the ${g('fundamental', 'fundamental')} vibration and turn it
        into a ${g('frequency', 'frequency')}.
      </li>
      <li>
        <strong>Read the needle.</strong> That frequency is named against your chosen
        ${g('reference-a', 'reference A')}, and the dial shows how many ${g('cents', 'cents')} sharp or
        flat you are. Green in the middle means in tune (±5 cents).
      </li>
      <li>
        <strong>Or tune by ear.</strong> Tap a string to hear its exact reference tone, or download the
        whole ${g('pitch-pipe', 'pitch pipe')} as a WAV — a file you can keep and use anywhere, even
        offline with no microphone.
      </li>
      <li>
        <strong>Check a recording.</strong> Drop in an audio clip and Tunewell charts its pitch over
        time — handy for spotting a note that drifts.
      </li>
    </ol>
    <p class="modal-foot-note">Every step runs on this device. The pitch maths is built into the page;
    there is no server in the loop.</p>
  `;
}

export function privacyHtml(): string {
  return /* html */ `
    <p>Tunewell is a listening instrument, not a recorder. The honest version of "your audio never
    leaves the device" is that the microphone is turned into a single frequency number, slice by
    slice, and immediately forgotten.</p>

    <h3>Protected</h3>
    <ul class="tick">
      <li>Microphone audio is analysed in memory and discarded. It is never recorded to a file, never
      saved to disk, and never uploaded.</li>
      <li>Recordings you drop in are decoded and analysed locally. Their bytes never leave the tab.</li>
      <li>Reference tones and the downloadable ${g('pitch-pipe', 'pitch pipe')} are synthesised on the
      device — nothing is fetched to make them.</li>
      <li>No cookies, no fingerprinting, no third-party fonts, no accounts.</li>
    </ul>

    <h3>Not protected</h3>
    <ul class="cross">
      <li><strong>Absolute concert-pitch accuracy.</strong> A browser cannot read your microphone's
      hardware, and a cheap mic in a noisy room can pull the reading a little. The
      ${g('reference-a', 'reference-A')} control and the downloadable pipe are there for exactly this.
      It is a musician's tuner, not a laboratory frequency counter.</li>
      <li>The initial page load is served by GitHub Pages, whose CDN sees your IP address and the
      request, exactly like visiting any website.</li>
    </ul>

    <h3>Trust surface</h3>
    <ul class="dot-list">
      <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS connection to
      GitHub Pages.</li>
      <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no
      fingerprinting, no cross-site tracking; your audio is never sent to it.</li>
      <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you
      open the feedback form and press Send; your audio and data never are.</li>
    </ul>
  `;
}

export function aboutHtml(): string {
  return /* html */ `
    <p><strong>Tunewell</strong> is a chromatic instrument tuner that runs entirely in your browser — a
    live needle from your microphone, per-string lights for guitar, bass, ukulele and violin, and a
    downloadable reference-tone pitch pipe for tuning by ear.</p>
    <p>It is one of a series of small, private, no-account browser tools. See the rest at
    <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>
    <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
    Source and issues on <a href="https://github.com/ben-gy/tunewell" target="_blank" rel="noopener">GitHub</a>.</p>
    <p class="modal-foot-note">Found a bug or have an idea? Use the Feedback link in the footer — it goes
    straight to the project's issue tracker.</p>
  `;
}
